import React, { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useLightboxStore } from '@/store';
import Lightbox from '@/components/Lightbox';

export default function LightboxRoot() {
  const isOpen = useLightboxStore(state => state.isOpen);
  const imageUrl = useLightboxStore(state => state.imageUrl);
  const fileId = useLightboxStore(state => state.fileId);
  const conversationId = useLightboxStore(state => state.conversationId);
  const dismissOnOutsidePress = useLightboxStore(state => state.dismissOnOutsidePress);
  const openToEditor = useLightboxStore(state => state.openToEditor);
  const withInpainting = useLightboxStore(state => state.withInpainting);
  const withChatInput = useLightboxStore(state => state.withChatInput);
  const chatInputProps = useLightboxStore(state => state.chatInputProps);
  const { closeLightbox } = useLightboxStore(state => state.actions);

  // Web: let the browser Back button dismiss the lightbox instead of
  // navigating away from the conversation (Back used to land on gab.ai
  // home, losing the chat the media belonged to). A same-URL sentinel
  // entry is pushed while open; its pop means "close the lightbox".
  const sentinelPoppedRef = useRef(false);

  useEffect(() => {
    if (!isOpen || Platform.OS !== 'web' || typeof window === 'undefined') return undefined;

    sentinelPoppedRef.current = false;
    try {
      window.history.pushState({ gabLightbox: true }, '');
    } catch (_) { /* history unavailable — lightbox still works without Back integration */ }

    const handlePopState = () => {
      sentinelPoppedRef.current = true;
      closeLightbox();
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Closed via UI (X, overlay, Esc) rather than Back: the sentinel is
      // still on the stack, so drop it or a later Back would pop a phantom
      // entry. The pop this triggers is async and our listener is gone.
      if (!sentinelPoppedRef.current) {
        sentinelPoppedRef.current = true;
        try {
          window.history.back();
        } catch (_) { /* ignore */ }
      }
    };
  }, [isOpen, closeLightbox]);

  if (!isOpen) return null;

  return (
    <Lightbox 
      isOpen={isOpen}
      onClose={closeLightbox}
      imageUrl={imageUrl}
      fileId={fileId}
      conversationId={conversationId}
      openToEditor={openToEditor}
      dismissOnOutsidePress={dismissOnOutsidePress}
      withInpainting={withInpainting}
      withChatInput={withChatInput}
      chatInputProps={chatInputProps}
    />
  );
}
